/**
 * Lobby panels: create-table form, join form, room info, errors.
 * Plain DOM, no framework (phase1-plan §2); rendered into #panel and
 * unit-tested under jsdom.
 */

export interface CreateFormCallbacks {
  onCreate(tableName: string): void;
}

export function renderCreateForm(root: HTMLElement, cb: CreateFormCallbacks): void {
  root.replaceChildren();
  const form = document.createElement("form");
  form.dataset["testid"] = "create-form";

  const label = document.createElement("label");
  label.textContent = "Table name";
  const input = document.createElement("input");
  input.name = "tableName";
  input.placeholder = "New Table";
  label.appendChild(input);

  const button = document.createElement("button");
  button.type = "submit";
  button.textContent = "Create table";

  form.append(label, button);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    cb.onCreate(input.value);
  });
  root.appendChild(form);
}

export interface JoinFormCallbacks {
  onJoin(playerName: string): void;
}

export function renderJoinForm(
  root: HTMLElement,
  roomName: string,
  defaultName: string,
  cb: JoinFormCallbacks,
): void {
  root.replaceChildren();
  const heading = document.createElement("h2");
  heading.textContent = `Join “${roomName}”`;

  const form = document.createElement("form");
  form.dataset["testid"] = "join-form";

  const label = document.createElement("label");
  label.textContent = "Your name";
  const input = document.createElement("input");
  input.name = "playerName";
  input.value = defaultName;
  input.required = true;
  label.appendChild(input);

  const button = document.createElement("button");
  button.type = "submit";
  button.textContent = "Join table";

  form.append(label, button);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    cb.onJoin(input.value);
  });
  root.append(heading, form);
}

export interface RoomInfo {
  code: string;
  joinUrl: string;
  /** Present only right after creation — shown once to the GM. */
  gmKey?: string | undefined;
}

export function renderRoomInfo(root: HTMLElement, info: RoomInfo): void {
  const panel = document.createElement("div");
  panel.dataset["testid"] = "room-info";

  const code = document.createElement("p");
  code.textContent = `Room code: ${info.code}`;
  const link = document.createElement("p");
  link.textContent = `Join link: ${info.joinUrl}`;
  panel.append(code, link);

  if (info.gmKey !== undefined) {
    const key = document.createElement("p");
    key.dataset["testid"] = "gm-key";
    key.textContent = `GM key (save this — it is shown only once): ${info.gmKey}`;
    panel.appendChild(key);
  }
  root.appendChild(panel);
}

/**
 * Fatal-for-this-view errors (join failures, missing rooms). GENERIC by
 * contract (§6.2): this element carries page-level copy only and is not a
 * rejection surface — the typed code rides `data-reason` on the dedicated
 * `[data-testid="rejection"]` notice, and tests must bind there.
 */
export function renderError(root: HTMLElement, message: string): void {
  root.replaceChildren();
  const error = document.createElement("p");
  error.dataset["testid"] = "error";
  error.setAttribute("role", "alert");
  error.textContent = message;
  root.appendChild(error);
}
